import MsgReaderModule from '@kenjiuno/msgreader';
import PostalMime from 'postal-mime';
import { getFileExtension, getUniqueFileName } from '@/features/msGraph/helper';
import { MSG_TYPE } from '@/utils/folders';

export const isEmailFile = (file) => {
	if (!(file instanceof File)) return false;

	const mime = String(file.type ?? '').toLowerCase();
	const ext = getFileExtension(file.name);

	return mime === MSG_TYPE || mime === 'message/rfc822' || ext === 'msg' || ext === 'eml';
};

const toFile = (content, fileName, mimeType, knownFiles) => {
	const size = content?.byteLength ?? content?.length ?? 0;
	if (!fileName || !size) return null;

	return new File([content], getUniqueFileName(knownFiles, fileName), {
		type: mimeType || '',
		lastModified: Date.now(),
	});
};

const attachmentName = (index, extension = 'bin') =>
	`attachment-${index + 1}.${String(extension).replace(/^\./, '') || 'bin'}`;

/**
 * Outlook often puts a Content-ID on real attachments too.
 * Only skip hidden parts and images actually used via cid: in the HTML.
 */
const isEmbeddedMsgImage = (att, html) => {
	if (att.attachmentHidden) return true;
	if (!att.pidContentId || !html) return false;

	const cid = String(att.pidContentId).replace(/^<|>$/g, '');
	return Boolean(cid) && html.includes(`cid:${cid}`);
};

const extractMsgAttachments = async (emailFile, knownFiles) => {
	const arrayBuffer = await emailFile.arrayBuffer();
	const MsgReader = MsgReaderModule.default || MsgReaderModule;
	const reader = new MsgReader(arrayBuffer);
	const info = reader.getFileData();
	const html = info.bodyHtml || (info.html ? new TextDecoder().decode(info.html) : '') || '';
	const extracted = [];

	for (const [index, meta] of (info.attachments ?? []).entries()) {
		if (isEmbeddedMsgImage(meta, html)) continue;

		const attachment = reader.getAttachment(meta);
		const name =
			attachment.fileName ||
			meta.fileName ||
			meta.fileNameShort ||
			meta.name ||
			attachmentName(index, meta.extension);
		const file = toFile(attachment.content, name, meta.attachMimeTag || '', [
			...knownFiles,
			...extracted,
		]);

		if (file) extracted.push(file);
	}

	return extracted;
};

const extractEmlAttachments = async (emailFile, knownFiles) => {
	const arrayBuffer = await emailFile.arrayBuffer();
	const info = await PostalMime.parse(arrayBuffer);
	const attachments = info?.attachments ?? [];
	const extracted = [];

	for (const att of attachments) {
		if (att.disposition === 'inline' || att.related) continue;

		const file = toFile(att.content, att.filename, att.mimeType || '', [
			...knownFiles,
			...extracted,
		]);
		if (file) extracted.push(file);
	}

	return extracted;
};

const extractAttachments = async (emailFile, knownFiles) => {
	const ext = getFileExtension(emailFile.name);
	const mime = String(emailFile.type ?? '').toLowerCase();

	if (ext === 'msg' || mime === MSG_TYPE) {
		return extractMsgAttachments(emailFile, knownFiles);
	}
	if (ext === 'eml' || mime === 'message/rfc822') {
		return extractEmlAttachments(emailFile, knownFiles);
	}
	return [];
};

/**
 * Expands dropped files so .msg/.eml entries are followed by their extracted attachments.
 * Non-email files are returned unchanged. On parse failure, the original email file is kept.
 */
export const expandEmailFiles = async (files = [], existingFiles = []) => {
	const inputFiles = Array.isArray(files) ? files.filter((f) => f instanceof File) : [];
	const result = [];
	const knownNames = [...(existingFiles || [])];

	for (const file of inputFiles) {
		const uniqueName = getUniqueFileName(knownNames, file.name);
		const emailFile =
			uniqueName !== file.name ? new File([file], uniqueName, { type: file.type }) : file;

		result.push(emailFile);
		knownNames.push(emailFile);

		if (!isEmailFile(file)) continue;

		try {
			const attachments = await extractAttachments(emailFile, knownNames);
			result.push(...attachments);
			knownNames.push(...attachments);
		} catch (error) {
			console.error('❌ Failed extracting email attachments:', emailFile.name, error);
		}
	}

	return result;
};
